/**
 * RpgHubPage — the Mini-RPG home (ТЗ №5): hero profile (avatar / username / rank / level /
 * XP / hp / damage / weapon / balances / battle stats) and a grid of section entries.
 *
 * Fully server-driven: the profile comes from ``GET /api/rpg/profile/`` and the identity
 * (avatar / username) from ``GET /api/me/``. Everything shown is read-only here.
 */
import type { ReactNode } from "react";

import { CurrencyIcon } from "@melusine/shared-ui";
import { useBackButton } from "@melusine/shared-telegram";
import { formatCooldown, hpPercent, useRpgProfile, xpPercent } from "@entities/rpg";
import { displayName, useMe } from "@entities/user";
import { StatePanel } from "@widgets/catalog-browser";
import { formatUnits } from "@shared/lib/format";
import { routes, useNavigate } from "@shared/router";

import { RpgHeader } from "./ui/RpgHeader";
import { RpgIcon } from "./ui/RpgIcon";
import styles from "./rpg.module.css";

interface SectionTile {
  readonly label: string;
  readonly icon: ReactNode;
  readonly to: string;
  readonly primary?: boolean;
}

export function RpgHubPage(): JSX.Element {
  const navigate = useNavigate();
  const profileQuery = useRpgProfile();
  const meQuery = useMe();

  useBackButton(() => {
    navigate(routes.home());
  });

  if (profileQuery.isLoading) {
    return <StatePanel kind="loading" />;
  }
  if (profileQuery.error && profileQuery.data === null) {
    return (
      <StatePanel
        kind="error"
        message="Не удалось загрузить RPG."
        onRetry={profileQuery.refetch}
      />
    );
  }
  if (profileQuery.data === null) {
    return <StatePanel kind="empty" message="Нет данных" />;
  }

  const p = profileQuery.data;
  const me = meQuery.data;
  const name = me ? displayName(me) : "Герой";
  const avatar = me?.avatar_url ?? "";

  const tiles: readonly SectionTile[] = [
    { label: "Бой", icon: SwordsIcon, to: routes.rpgBattle(), primary: true },
    { label: "Развитие", icon: UpIcon, to: routes.rpgDevelop("weapons") },
    { label: "Рейтинг", icon: TrophyIcon, to: routes.rpgRatings() },
    { label: "Инвентарь", icon: BagIcon, to: routes.rpgDevelop("inventory") },
    { label: "Алхимия", icon: FlaskIcon, to: routes.rpgDevelop("alchemy") },
    { label: "Магазин", icon: CartIcon, to: routes.rpgDevelop("shop") },
    { label: "Обмен", icon: SwapIcon, to: routes.rpgExchange() },
    { label: "Переводы", icon: SendIcon, to: routes.rpgTransfers() },
    { label: "FAQ", icon: HelpIcon, to: routes.rpgFaq() },
  ];

  return (
    <div className={styles.page}>
      <RpgHeader title="Mini-RPG" onBack={() => navigate(routes.home())} />

      <section className={styles.profileCard}>
        <div className={styles.profileTop}>
          <span className={styles.avatar} aria-hidden="true">
            {avatar.trim().length > 0 ? (
              <img
                src={avatar}
                alt=""
                className={styles.avatarImg}
                referrerPolicy="no-referrer"
                draggable={false}
              />
            ) : (
              <span className={styles.avatarFallback}>
                {name.charAt(0).toUpperCase()}
              </span>
            )}
          </span>
          <div className={styles.profileMain}>
            <div className={styles.profileNameRow}>
              <span className={styles.profileName}>{name}</span>
              <span className={styles.rankBadge}>Ранг {p.rank}</span>
              <span className={styles.levelBadge}>Ур. {p.level}</span>
            </div>
            <div className={styles.xpBlock}>
              <div className={styles.xpHead}>
                <span className={styles.xpLabel}>Опыт</span>
                <span className={styles.xpValue}>
                  {formatUnits(p.xp)} / {formatUnits(p.xp_next)}
                </span>
              </div>
              <div className={styles.xpTrack}>
                <div
                  className={styles.xpFill}
                  style={{ width: `${xpPercent(p)}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.hpBlock}>
          <div className={styles.xpHead}>
            <span className={styles.xpLabel}>Здоровье</span>
            <span className={styles.xpValue}>
              {formatUnits(p.hp)} / {formatUnits(p.max_hp)}
            </span>
          </div>
          <div className={styles.xpTrack}>
            <div
              className={styles.hpFill}
              style={{ width: `${hpPercent(p)}%` }}
            />
          </div>
          <div className={styles.hpMeta}>
            <span>Бой: −{formatUnits(p.battle_hp_cost)} HP</span>
            <span>Реген: +{p.hp_regen_per_minute}/мин</span>
            {p.hp < p.max_hp && p.seconds_to_next_hp > 0 ? (
              <span>+1 HP через {formatCooldown(p.seconds_to_next_hp)}</span>
            ) : null}
          </div>
        </div>

        <div className={styles.statGrid}>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Урон</span>
            <span className={styles.statValue}>
              {formatUnits(p.effective_damage)}
            </span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Стоимость боя</span>
            <span className={styles.statValue}>
              −{formatUnits(p.battle_hp_cost)} HP
            </span>
          </div>
        </div>

        <div className={styles.weaponRow}>
          {p.equipped_weapon ? (
            <>
              <RpgIcon
                url={p.equipped_weapon.icon_url}
                name={p.equipped_weapon.name}
                size={40}
              />
              <div className={styles.weaponInfo}>
                <div className={styles.weaponName}>{p.equipped_weapon.name}</div>
                <div className={styles.weaponMeta}>
                  Ур. {p.equipped_weapon.level} · урон{" "}
                  {formatUnits(p.equipped_weapon.damage)}
                </div>
              </div>
            </>
          ) : (
            <div className={styles.weaponInfo}>
              <div className={styles.weaponName}>Оружие не экипировано</div>
              <div className={styles.weaponMeta}>
                Откройте «Развитие → Оружие»
              </div>
            </div>
          )}
        </div>

        <div className={styles.balanceRow}>
          <div className={styles.balanceItem}>
            <CurrencyIcon kind="mora" size={22} />
            <div className={styles.balanceText}>
              <span className={styles.balanceLabel}>Мора</span>
              <span className={styles.balanceValue}>{formatUnits(p.mora)}</span>
            </div>
          </div>
          <div className={styles.balanceItem}>
            <CurrencyIcon kind="primogem" size={22} />
            <div className={styles.balanceText}>
              <span className={styles.balanceLabel}>Примогемы</span>
              <span className={styles.balanceValue}>
                {formatUnits(p.primogems)}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.battleStats}>
          <span className={styles.battleStat}>
            Боёв: <b>{formatUnits(p.battles_count)}</b>
          </span>
          <span className={styles.battleStat}>
            Побед: <b>{formatUnits(p.wins)}</b>
          </span>
          <span className={styles.battleStat}>
            Боссов: <b>{formatUnits(p.boss_wins)}</b>
          </span>
          <span className={styles.battleStat}>
            Винрейт: <b>{p.win_rate}%</b>
          </span>
        </div>
      </section>

      <div className={styles.sectionGrid}>
        {tiles.map((tile) => (
          <button
            key={tile.label}
            type="button"
            className={`${styles.tile} ${tile.primary ? styles.tilePrimary : ""}`}
            onClick={() => navigate(tile.to)}
          >
            <span className={styles.tileIcon}>{tile.icon}</span>
            <span className={styles.tileLabel}>{tile.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---- Inline section glyphs (line-SVG, gold) ---- */
const SwordsIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14.5 17.5 3 6V3h3l11.5 11.5" />
    <path d="m13 19 6-6" />
    <path d="m16 16 4 4" />
    <path d="m19 21 2-2" />
    <path d="M21 3v3l-9 9" />
    <path d="m5 13-2 2" />
    <path d="m3 19 2 2" />
  </svg>
);
const UpIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20V4" />
    <path d="m5 11 7-7 7 7" />
  </svg>
);
const TrophyIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 9a6 6 0 0 0 12 0V3H6z" />
    <path d="M6 5H3v2a3 3 0 0 0 3 3" />
    <path d="M18 5h3v2a3 3 0 0 1-3 3" />
    <path d="M12 15v3" />
    <path d="M8 21h8" />
    <path d="M9 18h6" />
  </svg>
);
const BagIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 7h12l-1 13H7z" />
    <path d="M9 7a3 3 0 0 1 6 0" />
  </svg>
);
const FlaskIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 3h6" />
    <path d="M10 3v6l-5 9a2 2 0 0 0 2 3h10a2 2 0 0 0 2-3l-5-9V3" />
    <path d="M7 16h10" />
  </svg>
);
const CartIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="9" cy="20" r="1.4" />
    <circle cx="18" cy="20" r="1.4" />
    <path d="M2 3h3l2.2 12.4a1 1 0 0 0 1 .8h8.6a1 1 0 0 0 1-.8L21 7H6" />
  </svg>
);
const SwapIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7 4 3 8l4 4" />
    <path d="M3 8h13" />
    <path d="m17 20 4-4-4-4" />
    <path d="M21 16H8" />
  </svg>
);
const SendIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 2 11 13" />
    <path d="M22 2 15 22l-4-9-9-4z" />
  </svg>
);
const HelpIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M9.2 9a2.8 2.8 0 0 1 5.4 1c0 2-2.6 2.5-2.6 4" />
    <path d="M12 17h.01" />
  </svg>
);
